import React, { useState } from 'react';
import {
  Box, Typography, Card, CardContent, Stepper, Step, StepLabel,
  Button, Alert, Grid, Paper, Accordion, AccordionSummary,
  AccordionDetails, Chip, IconButton, Tooltip, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField,
  TableContainer, Table, TableHead, TableBody, TableRow, TableCell
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Code as CodeIcon,
  Download as DownloadIcon,
  ContentCopy as CopyIcon,
  Storage as DatabaseIcon,
  TableChart as TableIcon,
  Link as LinkIcon
} from '@mui/icons-material';
import ModernLayout from '../common/ModernLayout';
import { GradientButton } from '../common/ModernButton';
import { useLanguage } from '../../contexts/LanguageContext';

const DatabaseUpgradeWizard = () => {
  const { translate } = useLanguage();
  const [activeStep, setActiveStep] = useState(0);
  const [showSqlDialog, setShowSqlDialog] = useState(false);
  const [selectedTable, setSelectedTable] = useState(null);

  const steps = [
    'Database Overview',
    'New Tables',
    'UniFi Integration',
    'Employee Relations',
    'Migration Scripts',
    'API Endpoints'
  ];

  const databaseSchema = {
    tasks: {
      description: 'Task management system',
      columns: [
        { name: 'id', type: 'INTEGER PRIMARY KEY AUTOINCREMENT', description: 'Unique task identifier' },
        { name: 'title', type: 'VARCHAR(255) NOT NULL', description: 'Task title' },
        { name: 'description', type: 'TEXT', description: 'Task description' },
        { name: 'status', type: 'ENUM("pending", "in_progress", "completed", "overdue") DEFAULT "pending"', description: 'Task status' },
        { name: 'priority', type: 'ENUM("low", "medium", "high") DEFAULT "medium"', description: 'Task priority' },
        { name: 'assignee_id', type: 'INTEGER', description: 'Employee assigned to task' },
        { name: 'project_id', type: 'INTEGER', description: 'Project this task belongs to' },
        { name: 'estimated_hours', type: 'DECIMAL(8,2) DEFAULT 0', description: 'Estimated hours to complete' },
        { name: 'actual_hours', type: 'DECIMAL(8,2) DEFAULT 0', description: 'Actual hours spent' },
        { name: 'budget', type: 'DECIMAL(10,2) DEFAULT 0', description: 'Task budget' },
        { name: 'spent', type: 'DECIMAL(10,2) DEFAULT 0', description: 'Amount spent on task' },
        { name: 'due_date', type: 'DATE', description: 'Task due date' },
        { name: 'tags', type: 'JSON', description: 'Task tags' },
        { name: 'created_at', type: 'TIMESTAMP DEFAULT CURRENT_TIMESTAMP', description: 'Creation timestamp' },
        { name: 'updated_at', type: 'TIMESTAMP DEFAULT CURRENT_TIMESTAMP ON UPDATE CURRENT_TIMESTAMP', description: 'Last update timestamp' }
      ],
      indexes: [
        'CREATE INDEX idx_tasks_assignee ON tasks(assignee_id)',
        'CREATE INDEX idx_tasks_project ON tasks(project_id)',
        'CREATE INDEX idx_tasks_status ON tasks(status)',
        'CREATE INDEX idx_tasks_due_date ON tasks(due_date)'
      ]
    },
    projects: {
      description: 'Project management',
      columns: [
        { name: 'id', type: 'INTEGER PRIMARY KEY AUTOINCREMENT', description: 'Unique project identifier' },
        { name: 'name', type: 'VARCHAR(255) NOT NULL', description: 'Project name' },
        { name: 'description', type: 'TEXT', description: 'Project description' },
        { name: 'budget', type: 'DECIMAL(12,2) DEFAULT 0', description: 'Project budget' },
        { name: 'spent', type: 'DECIMAL(12,2) DEFAULT 0', description: 'Amount spent on project' },
        { name: 'start_date', type: 'DATE', description: 'Project start date' },
        { name: 'end_date', type: 'DATE', description: 'Project end date' },
        { name: 'status', type: 'ENUM("planning", "active", "completed", "cancelled") DEFAULT "planning"', description: 'Project status' },
        { name: 'manager_id', type: 'INTEGER', description: 'Project manager' },
        { name: 'created_at', type: 'TIMESTAMP DEFAULT CURRENT_TIMESTAMP', description: 'Creation timestamp' },
        { name: 'updated_at', type: 'TIMESTAMP DEFAULT CURRENT_TIMESTAMP ON UPDATE CURRENT_TIMESTAMP', description: 'Last update timestamp' }
      ],
      indexes: [
        'CREATE INDEX idx_projects_manager ON projects(manager_id)',
        'CREATE INDEX idx_projects_status ON projects(status)'
      ]
    },
    employees: {
      description: 'Employee information',
      columns: [
        { name: 'id', type: 'INTEGER PRIMARY KEY AUTOINCREMENT', description: 'Unique employee identifier' },
        { name: 'name', type: 'VARCHAR(255) NOT NULL', description: 'Employee name' },
        { name: 'email', type: 'VARCHAR(255) UNIQUE NOT NULL', description: 'Employee email' },
        { name: 'role', type: 'VARCHAR(100) NOT NULL', description: 'Employee role' },
        { name: 'department', type: 'VARCHAR(100) NOT NULL', description: 'Employee department' },
        { name: 'hourly_rate', type: 'DECIMAL(8,2) DEFAULT 0', description: 'Hourly rate' },
        { name: 'max_hours_per_week', type: 'INTEGER DEFAULT 40', description: 'Maximum hours per week' },
        { name: 'hire_date', type: 'DATE', description: 'Hire date' },
        { name: 'is_active', type: 'BOOLEAN DEFAULT TRUE', description: 'Active status' },
        { name: 'created_at', type: 'TIMESTAMP DEFAULT CURRENT_TIMESTAMP', description: 'Creation timestamp' },
        { name: 'updated_at', type: 'TIMESTAMP DEFAULT CURRENT_TIMESTAMP ON UPDATE CURRENT_TIMESTAMP', description: 'Last update timestamp' }
      ],
      indexes: [
        'CREATE INDEX idx_employees_department ON employees(department)',
        'CREATE INDEX idx_employees_role ON employees(role)',
        'CREATE INDEX idx_employees_active ON employees(is_active)'
      ]
    },
    time_logs: {
      description: 'Time tracking for tasks',
      columns: [
        { name: 'id', type: 'INTEGER PRIMARY KEY AUTOINCREMENT', description: 'Unique time log identifier' },
        { name: 'task_id', type: 'INTEGER NOT NULL', description: 'Task being logged' },
        { name: 'employee_id', type: 'INTEGER NOT NULL', description: 'Employee logging time' },
        { name: 'hours', type: 'DECIMAL(4,2) NOT NULL', description: 'Hours logged' },
        { name: 'description', type: 'TEXT', description: 'Description of work done' },
        { name: 'date', type: 'DATE NOT NULL', description: 'Date of work' },
        { name: 'created_at', type: 'TIMESTAMP DEFAULT CURRENT_TIMESTAMP', description: 'Creation timestamp' }
      ],
      indexes: [
        'CREATE INDEX idx_time_logs_task ON time_logs(task_id)',
        'CREATE INDEX idx_time_logs_employee ON time_logs(employee_id)',
        'CREATE INDEX idx_time_logs_date ON time_logs(date)'
      ]
    },
    expenses: {
      description: 'Project expenses tracking',
      columns: [
        { name: 'id', type: 'INTEGER PRIMARY KEY AUTOINCREMENT', description: 'Unique expense identifier' },
        { name: 'project_id', type: 'INTEGER NOT NULL', description: 'Project this expense belongs to' },
        { name: 'amount', type: 'DECIMAL(10,2) NOT NULL', description: 'Expense amount' },
        { name: 'description', type: 'TEXT NOT NULL', description: 'Expense description' },
        { name: 'category', type: 'VARCHAR(100) NOT NULL', description: 'Expense category' },
        { name: 'date', type: 'DATE NOT NULL', description: 'Expense date' },
        { name: 'approved_by', type: 'INTEGER', description: 'Employee who approved expense' },
        { name: 'created_at', type: 'TIMESTAMP DEFAULT CURRENT_TIMESTAMP', description: 'Creation timestamp' }
      ],
      indexes: [
        'CREATE INDEX idx_expenses_project ON expenses(project_id)',
        'CREATE INDEX idx_expenses_category ON expenses(category)',
        'CREATE INDEX idx_expenses_date ON expenses(date)'
      ]
    },
    unifi_devices: {
      description: 'UniFi network devices',
      columns: [
        { name: 'id', type: 'INTEGER PRIMARY KEY AUTOINCREMENT', description: 'Unique device identifier' },
        { name: 'unifi_id', type: 'VARCHAR(255) UNIQUE NOT NULL', description: 'UniFi device ID' },
        { name: 'name', type: 'VARCHAR(255) NOT NULL', description: 'Device name' },
        { name: 'type', type: 'VARCHAR(100) NOT NULL', description: 'Device type (AP, Switch, Gateway, etc.)' },
        { name: 'model', type: 'VARCHAR(100)', description: 'Device model' },
        { name: 'mac_address', type: 'VARCHAR(17)', description: 'MAC address' },
        { name: 'ip_address', type: 'VARCHAR(45)', description: 'IP address' },
        { name: 'status', type: 'ENUM("online", "offline", "disconnected") DEFAULT "offline"', description: 'Device status' },
        { name: 'site_id', type: 'VARCHAR(255)', description: 'UniFi site ID' },
        { name: 'last_seen', type: 'TIMESTAMP', description: 'Last time device was seen' },
        { name: 'created_at', type: 'TIMESTAMP DEFAULT CURRENT_TIMESTAMP', description: 'Creation timestamp' },
        { name: 'updated_at', type: 'TIMESTAMP DEFAULT CURRENT_TIMESTAMP ON UPDATE CURRENT_TIMESTAMP', description: 'Last update timestamp' }
      ],
      indexes: [
        'CREATE INDEX idx_unifi_devices_type ON unifi_devices(type)',
        'CREATE INDEX idx_unifi_devices_status ON unifi_devices(status)',
        'CREATE INDEX idx_unifi_devices_site ON unifi_devices(site_id)'
      ]
    },
    unifi_access_logs: {
      description: 'UniFi access control logs',
      columns: [
        { name: 'id', type: 'INTEGER PRIMARY KEY AUTOINCREMENT', description: 'Unique log identifier' },
        { name: 'device_id', type: 'INTEGER NOT NULL', description: 'UniFi device ID' },
        { name: 'employee_id', type: 'INTEGER', description: 'Employee who accessed (if known)' },
        { name: 'action', type: 'ENUM("unlock", "lock", "access_granted", "access_denied") NOT NULL', description: 'Action performed' },
        { name: 'timestamp', type: 'TIMESTAMP NOT NULL', description: 'When action occurred' },
        { name: 'user_identifier', type: 'VARCHAR(255)', description: 'User identifier (card, fingerprint, etc.)' },
        { name: 'location', type: 'VARCHAR(255)', description: 'Location/area accessed' },
        { name: 'notes', type: 'TEXT', description: 'Additional notes' },
        { name: 'created_at', type: 'TIMESTAMP DEFAULT CURRENT_TIMESTAMP', description: 'Creation timestamp' }
      ],
      indexes: [
        'CREATE INDEX idx_access_logs_device ON unifi_access_logs(device_id)',
        'CREATE INDEX idx_access_logs_employee ON unifi_access_logs(employee_id)',
        'CREATE INDEX idx_access_logs_timestamp ON unifi_access_logs(timestamp)',
        'CREATE INDEX idx_access_logs_action ON unifi_access_logs(action)'
      ]
    },
    employee_access_cards: {
      description: 'Employee access card mapping',
      columns: [
        { name: 'id', type: 'INTEGER PRIMARY KEY AUTOINCREMENT', description: 'Unique mapping identifier' },
        { name: 'employee_id', type: 'INTEGER NOT NULL', description: 'Employee ID' },
        { name: 'card_identifier', type: 'VARCHAR(255) UNIQUE NOT NULL', description: 'Card/fob identifier' },
        { name: 'card_type', type: 'ENUM("rfid", "nfc", "magnetic", "barcode") NOT NULL', description: 'Type of access card' },
        { name: 'is_active', type: 'BOOLEAN DEFAULT TRUE', description: 'Whether card is active' },
        { name: 'issued_date', type: 'DATE', description: 'When card was issued' },
        { name: 'expiry_date', type: 'DATE', description: 'When card expires' },
        { name: 'created_at', type: 'TIMESTAMP DEFAULT CURRENT_TIMESTAMP', description: 'Creation timestamp' },
        { name: 'updated_at', type: 'TIMESTAMP DEFAULT CURRENT_TIMESTAMP ON UPDATE CURRENT_TIMESTAMP', description: 'Last update timestamp' }
      ],
      indexes: [
        'CREATE INDEX idx_access_cards_employee ON employee_access_cards(employee_id)',
        'CREATE INDEX idx_access_cards_identifier ON employee_access_cards(card_identifier)',
        'CREATE INDEX idx_access_cards_active ON employee_access_cards(is_active)'
      ]
    }
  };

  const foreignKeys = [
    'ALTER TABLE tasks ADD CONSTRAINT fk_tasks_assignee FOREIGN KEY (assignee_id) REFERENCES employees(id)',
    'ALTER TABLE tasks ADD CONSTRAINT fk_tasks_project FOREIGN KEY (project_id) REFERENCES projects(id)',
    'ALTER TABLE projects ADD CONSTRAINT fk_projects_manager FOREIGN KEY (manager_id) REFERENCES employees(id)',
    'ALTER TABLE time_logs ADD CONSTRAINT fk_time_logs_task FOREIGN KEY (task_id) REFERENCES tasks(id)',
    'ALTER TABLE time_logs ADD CONSTRAINT fk_time_logs_employee FOREIGN KEY (employee_id) REFERENCES employees(id)',
    'ALTER TABLE expenses ADD CONSTRAINT fk_expenses_project FOREIGN KEY (project_id) REFERENCES projects(id)',
    'ALTER TABLE expenses ADD CONSTRAINT fk_expenses_approved_by FOREIGN KEY (approved_by) REFERENCES employees(id)',
    'ALTER TABLE unifi_access_logs ADD CONSTRAINT fk_access_logs_device FOREIGN KEY (device_id) REFERENCES unifi_devices(id)',
    'ALTER TABLE unifi_access_logs ADD CONSTRAINT fk_access_logs_employee FOREIGN KEY (employee_id) REFERENCES employees(id)',
    'ALTER TABLE employee_access_cards ADD CONSTRAINT fk_access_cards_employee FOREIGN KEY (employee_id) REFERENCES employees(id)'
  ];

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
  };

  const generateCreateTableSQL = (tableName, tableData) => {
    const columns = tableData.columns.map(col => `  ${col.name} ${col.type}`).join(',\n');
    const indexes = tableData.indexes.join('\n');
    
    return `-- Create ${tableName} table
CREATE TABLE ${tableName} (
${columns}
);

-- Create indexes
${indexes}
`;
  };

  const generateFullSQL = () => {
    let sql = `-- HarvestAI Database Upgrade Script
-- Generated: ${new Date().toISOString()}
-- Description: Task Management System with UniFi Integration

-- Create new tables
`;

    Object.entries(databaseSchema).forEach(([tableName, tableData]) => {
      sql += generateCreateTableSQL(tableName, tableData);
      sql += '\n';
    });

    sql += `-- Add foreign key constraints
`;

    foreignKeys.forEach(fk => {
      sql += `${fk};\n`;
    });

    return sql;
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Database Overview
            </Typography>
            <Alert severity="info" sx={{ mb: 3 }}>
              This upgrade adds a comprehensive task management system with UniFi integration.
              The new system includes task tracking, budget management, time logging, and employee access control.
            </Alert>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      New Tables ({Object.keys(databaseSchema).length})
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {Object.entries(databaseSchema).map(([tableName, tableData]) => (
                        <Box key={tableName} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TableIcon color="primary" fontSize="small" />
                          <Typography variant="body2">{tableName}</Typography>
                          <Chip label={tableData.columns.length} size="small" />
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Key Features
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckIcon color="success" fontSize="small" />
                        <Typography variant="body2">Task Management with Status Tracking</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckIcon color="success" fontSize="small" />
                        <Typography variant="body2">Budget and Expense Tracking</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckIcon color="success" fontSize="small" />
                        <Typography variant="body2">Time Logging and Efficiency Analysis</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckIcon color="success" fontSize="small" />
                        <Typography variant="body2">UniFi Device Integration</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckIcon color="success" fontSize="small" />
                        <Typography variant="body2">Employee Access Control Logging</Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              New Tables Schema
            </Typography>
            
            {Object.entries(databaseSchema).map(([tableName, tableData]) => (
              <Accordion key={tableName} sx={{ mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    <TableIcon color="primary" />
                    <Typography variant="h6">{tableName}</Typography>
                    <Chip label={tableData.columns.length} size="small" />
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
                      {tableData.description}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Columns:
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Column</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Description</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {tableData.columns.map((column) => (
                            <TableRow key={column.name}>
                              <TableCell>
                                <Typography variant="body2" fontWeight="medium">
                                  {column.name}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontFamily="monospace">
                                  {column.type}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="text.secondary">
                                  {column.description}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                  
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Indexes:
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {tableData.indexes.map((index, idx) => (
                        <Typography key={idx} variant="body2" fontFamily="monospace" sx={{ bgcolor: 'grey.100', p: 1, borderRadius: 1 }}>
                          {index}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              UniFi Integration
            </Typography>
            
            <Alert severity="info" sx={{ mb: 3 }}>
              The UniFi integration moves from frontend-only to backend integration, allowing for proper
              employee access tracking and historical data analysis.
            </Alert>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      UniFi Devices Table
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Stores all UniFi network devices (APs, switches, gateways, access control devices)
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Typography variant="body2">• Device identification and status</Typography>
                      <Typography variant="body2">• Site and location tracking</Typography>
                      <Typography variant="body2">• Real-time status monitoring</Typography>
                      <Typography variant="body2">• Integration with access control</Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Access Logs Table
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Tracks all access control events (door unlocks, access attempts, etc.)
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Typography variant="body2">• Employee access tracking</Typography>
                      <Typography variant="body2">• Device and location logging</Typography>
                      <Typography variant="body2">• Security audit trail</Typography>
                      <Typography variant="body2">• Time-based access analysis</Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        );

      case 3:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Employee Relations
            </Typography>
            
            <Alert severity="warning" sx={{ mb: 3 }}>
              This creates the foundation for tracking which employees access which doors and when.
            </Alert>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Employee Access Cards
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Maps employee IDs to their access cards/fobs for door access tracking
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Typography variant="body2">• RFID/NFC card mapping</Typography>
                      <Typography variant="body2">• Card activation/deactivation</Typography>
                      <Typography variant="body2">• Expiry date management</Typography>
                      <Typography variant="body2">• Multiple card support</Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Access Log Relations
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Links access logs to employees for comprehensive tracking
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Typography variant="body2">• Employee → Access Log</Typography>
                      <Typography variant="body2">• Device → Access Log</Typography>
                      <Typography variant="body2">• Time-based access patterns</Typography>
                      <Typography variant="body2">• Security incident tracking</Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        );

      case 4:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Migration Scripts
            </Typography>
            
            <Alert severity="success" sx={{ mb: 3 }}>
              Use these scripts to safely upgrade your database. Always backup your data first!
            </Alert>
            
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6">Complete SQL Script</Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          startIcon={<DownloadIcon />}
                          variant="outlined"
                          onClick={() => {
                            const sql = generateFullSQL();
                            const blob = new Blob([sql], { type: 'text/plain' });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = 'harvestai_database_upgrade.sql';
                            a.click();
                          }}
                        >
                          Download SQL
                        </Button>
                        <Button
                          startIcon={<CopyIcon />}
                          variant="outlined"
                          onClick={() => copyToClipboard(generateFullSQL())}
                        >
                          Copy SQL
                        </Button>
                      </Box>
                    </Box>
                    
                    <Paper variant="outlined" sx={{ p: 2, maxHeight: 400, overflow: 'auto' }}>
                      <Typography variant="body2" fontFamily="monospace" component="pre" sx={{ whiteSpace: 'pre-wrap' }}>
                        {generateFullSQL()}
                      </Typography>
                    </Paper>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        );

      case 5:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Required API Endpoints
            </Typography>
            
            <Alert severity="info" sx={{ mb: 3 }}>
              Implement these API endpoints to support the frontend task management system.
            </Alert>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Task Management APIs
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Typography variant="body2" fontFamily="monospace">GET /api/tasks</Typography>
                      <Typography variant="body2" fontFamily="monospace">POST /api/tasks</Typography>
                      <Typography variant="body2" fontFamily="monospace">PUT /api/tasks/:id</Typography>
                      <Typography variant="body2" fontFamily="monospace">DELETE /api/tasks/:id</Typography>
                      <Typography variant="body2" fontFamily="monospace">POST /api/tasks/:id/time-log</Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      UniFi Integration APIs
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Typography variant="body2" fontFamily="monospace">GET /api/unifi/devices</Typography>
                      <Typography variant="body2" fontFamily="monospace">GET /api/unifi/access-logs</Typography>
                      <Typography variant="body2" fontFamily="monospace">POST /api/unifi/devices/:id/unlock</Typography>
                      <Typography variant="body2" fontFamily="monospace">GET /api/employees/:id/access-history</Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Additional APIs
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Typography variant="body2" fontFamily="monospace">GET /api/projects</Typography>
                      <Typography variant="body2" fontFamily="monospace">GET /api/employees</Typography>
                      <Typography variant="body2" fontFamily="monospace">GET /api/expenses</Typography>
                      <Typography variant="body2" fontFamily="monospace">POST /api/expenses</Typography>
                      <Typography variant="body2" fontFamily="monospace">GET /api/reports/budget</Typography>
                      <Typography variant="body2" fontFamily="monospace">GET /api/reports/workload</Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        );

      default:
        return 'Unknown step';
    }
  };

  return (
    <ModernLayout title="Database Upgrade Wizard">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
          Database Upgrade Wizard
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Upgrade your database to support the new task management and UniFi integration features
        </Typography>
      </Box>

      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {renderStepContent()}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
            >
              Back
            </Button>
            <Box>
              {activeStep === steps.length - 1 ? (
                <Button
                  variant="contained"
                  onClick={handleReset}
                >
                  Reset
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleNext}
                >
                  Next
                </Button>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
    </ModernLayout>
  );
};

export default DatabaseUpgradeWizard; 